import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPublishedArticles, Article } from '../../../utils/articleApi';
import { articleHasCategory, getArticleCategoryList } from '../../../utils/articleCategories';
import ArticleCategoryBadges from '../../../components/feature/ArticleCategoryBadges';
import AuthorAvatar from '../../../components/feature/AuthorAvatar';

const CATEGORY_GRID_INITIAL = 6;
const CATEGORY_GRID_STEP = 6;

interface ArticleCategoriesProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
}

export default function ArticleCategories({ selectedCategory, onCategoryChange }: ArticleCategoriesProps) {
  const [publishedArticles, setPublishedArticles] = useState<Article[]>([]);
  const [visibleCount, setVisibleCount] = useState(CATEGORY_GRID_INITIAL);

  useEffect(() => {
    setVisibleCount(CATEGORY_GRID_INITIAL);
  }, [selectedCategory]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const articles = await fetchPublishedArticles();
      const published = articles.filter((a) => a.status === '公開');
      if (!cancelled) {
        setPublishedArticles(published);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = Array.from(
    new Set(publishedArticles.flatMap((a) => getArticleCategoryList(a)))
  ).sort();

  const filteredArticles =
    selectedCategory === 'すべて'
      ? publishedArticles
      : publishedArticles.filter((article) => articleHasCategory(article, selectedCategory));

  const visibleArticles = filteredArticles.slice(0, visibleCount);
  const hasMoreInCategory = visibleCount < filteredArticles.length;
  const remainingCount = filteredArticles.length - visibleCount;

  return (
    <section id="categories" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#111827] mb-6">
            カテゴリから探す
          </h2>
          <p className="text-xl text-[#6B7280] max-w-2xl mx-auto">
            興味のあるカテゴリーを選んで旅行記事を絞り込めます
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-16">
          <button
            type="button"
            onClick={() => onCategoryChange('すべて')}
            className={`px-8 py-4 rounded-full text-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
              selectedCategory === 'すべて'
                ? 'bg-[#14B8A6] text-white shadow-lg'
                : 'bg-[#F8FAFC] text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]'
            }`}
          >
            すべて ({publishedArticles.length})
          </button>
          {categories.map((category) => {
            const categoryCount = publishedArticles.filter((article) =>
              articleHasCategory(article, category)
            ).length;
            return (
              <button
                type="button"
                key={category}
                onClick={() => onCategoryChange(category)}
                className={`px-8 py-4 rounded-full text-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-[#14B8A6] text-white shadow-lg'
                    : 'bg-[#F8FAFC] text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]'
                }`}
              >
                {category} ({categoryCount})
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {visibleArticles.map((article) => (
            <Link
              key={article.id}
              to={`/articles/${article.id}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-[#E5E7EB]"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              <div className="p-6">
                <div className="mb-3">
                  <ArticleCategoryBadges article={article} badgeClassName="inline-block bg-[#14B8A6] text-white px-3 py-1 rounded-full text-sm font-medium" />
                </div>

                <h3 className="text-xl font-bold text-[#111827] mb-3 group-hover:text-[#14B8A6] transition-colors line-clamp-2">
                  {article.title}
                </h3>

                <p className="text-[#6B7280] mb-4 line-clamp-3">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between text-sm text-[#6B7280]">
                  <div className="flex items-center space-x-2">
                    <AuthorAvatar size="md" />
                    <span>{article.author}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center space-x-1">
                      <i className="ri-time-line"></i>
                      <span>{article.readTime}</span>
                    </span>
                    <span>{article.publishDate}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {hasMoreInCategory && (
          <div className="flex justify-center mt-12">
            <button
              type="button"
              onClick={() =>
                setVisibleCount((c) => Math.min(c + CATEGORY_GRID_STEP, filteredArticles.length))
              }
              className="px-10 py-4 rounded-full text-lg font-medium bg-[#F8FAFC] text-[#111827] border border-[#E5E7EB] hover:bg-[#E5E7EB] transition-colors duration-200 cursor-pointer"
            >
              さらに表示（残り{remainingCount}件）
            </button>
          </div>
        )}

        {filteredArticles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-[#F3F4F6] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-article-line text-3xl text-[#9CA3AF]"></i>
            </div>
            <h3 className="text-2xl font-medium text-[#111827] mb-3">旅行記事が見つかりません</h3>
            <p className="text-lg text-[#6B7280]">他のカテゴリーをお試しください</p>
          </div>
        )}
      </div>
    </section>
  );
}
